import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Grid,
  FormControl,
  FormHelperText,
  TextField,
  Typography,
  Link
} from '@mui/material';
import {
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import api from 'utils/api';
import { fetchUser } from 'modules/User/actions';
import { fetchBusiness } from 'modules/Business/actions';
import { PaymentSchema } from './utils';
import cardImg from './cards.png';
import { saveAutoReloadPreference } from 'utils/businessAutoReload';
import AutoReloadPreference, {
  FORM_COLUMN_AUTO_RELOAD_SX,
  useAutoReloadPreference,
} from 'modules/Business/components/AutoReloadPreference';

const PaymentForm = ({
  businessId,
  onClose,
  onSuccess,
  autoReloadPreference: controlledAutoReloadPreference,
  showAutoReloadPreference = true,
}) => {
  const dispatch = useDispatch();
  const [posting, setPosting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const stripe = useStripe();
  const elements = useElements();
  const business = useSelector(state => state.business);
  const internalAutoReloadPreference = useAutoReloadPreference(business);
  const autoReloadPreference = controlledAutoReloadPreference
    ?? internalAutoReloadPreference;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(PaymentSchema),
    defaultValues: {
      name: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
    },
  });

  const handleFormSubmit = async (data) => {
    if (!stripe || !elements) {
      enqueueSnackbar('Stripe is not loaded. Please try again later.', { variant: 'error' });
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);
    if (!cardElement) {
      enqueueSnackbar('Card details are missing.', { variant: 'error' });
      return;
    }

    const payload = {
      name: data.name,
      address_line1: data.address,
      address_city: data.city,
      address_state: data.state,
      address_zip: data.zipCode,
      address_country: data.country,
    };

    try {
      setPosting(true);
      const { token } = await stripe.createToken(cardElement, payload);

      if (!token) {
        enqueueSnackbar('Invalid card details. Please check and try again.', { variant: 'error' });
        return;
      }

      await api.post('/stripe/customer', {
        token: token.id,
        businessId,
      });
      dispatch(fetchUser());

      try {
        await saveAutoReloadPreference(
          businessId,
          autoReloadPreference.reloadAmount
        );
      } catch {
        enqueueSnackbar(
          'Payment method added, but auto reload could not be updated.',
          { variant: 'warning' }
        );
      }
      await dispatch(fetchBusiness(businessId, true));
      enqueueSnackbar('Payment method added successfully!', { variant: 'success' });
      onSuccess?.();
      onClose?.();
    } catch (err) {
      console.error('Error adding payment method:', err);
      enqueueSnackbar('Cannot add payment method. Please try again.', { variant: 'error' });
    } finally {
      setPosting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      {showAutoReloadPreference && (
        <AutoReloadPreference
          preference={autoReloadPreference}
          sx={{ ...FORM_COLUMN_AUTO_RELOAD_SX, mb: 3 }}
        />
      )}

      <Typography variant="body2" color="textSecondary">
        We use <Link href="https://www.stripe.com/" target="_blank" rel="nofollow">Stripe</Link> as our secure payment processor. 
        By adding your payment information to your account you enable your offers to run uninterrupted. 
        Add, Update or Remove your payment methods at any time.
      </Typography>
      
      <Box py={2}>
        <img src={cardImg} alt="cards" height={30} />
      </Box>

      {/* Card Details */}
      <Box my={2}>
        <Typography variant="body2">Card number</Typography>
        <Box
          sx={{
            border: "1px solid rgba(0, 0, 0, 0.23)",
            borderRadius: "4px",
            padding: "10px",
          }}
        >
          <CardNumberElement options={{ style: { base: { fontSize: "16px" } } } }/>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Typography variant="body2">Expiration date</Typography>
          <Box
            sx={{
              border: "1px solid rgba(0, 0, 0, 0.23)",
              borderRadius: "4px",
              padding: "10px",
            }}
          >
            <CardExpiryElement options={{ style: { base: { fontSize: "16px" } } } }/>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body2">CVC</Typography>
          <Box
            sx={{
              border: "1px solid rgba(0, 0, 0, 0.23)",
              borderRadius: "4px",
              padding: "10px",
            }}
          >
            <CardCvcElement options={{ style: { base: { fontSize: "16px" } } } }/>
          </Box>
        </Grid>
      </Grid>

      {/* Billing Address */}
      <Box mt={2}>
        <Typography variant="subtitle1">Billing address</Typography>
      </Box>

      <Box my={2}>
        <FormControl fullWidth error={!!errors.name}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Name"
                variant="outlined"
                size="small"
              />
            )}
          />
          <FormHelperText>{errors.name?.message}</FormHelperText>
        </FormControl>
      </Box>

      <Box my={2}>
        <FormControl fullWidth error={!!errors.address}>
          <Controller
            name="address"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Address"
                variant="outlined"
                size="small"
              />
            )}
          />
          <FormHelperText>{errors.address?.message}</FormHelperText>
        </FormControl>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.city}>
            <Controller
              name="city"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="City"
                  variant="outlined"
                  size="small"
                />
              )}
            />
            <FormHelperText>{errors.city?.message}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.state}>
            <Controller
              name="state"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="State"
                  variant="outlined"
                  size="small"
                />
              )}
            />
            <FormHelperText>{errors.state?.message}</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>

      <Grid container spacing={3} mt={2}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.zipCode}>
            <Controller
              name="zipCode"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Zip code"
                  variant="outlined"
                  size="small"
                />
              )}
            />
            <FormHelperText>{errors.zipCode?.message}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.country}>
            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Country"
                  variant="outlined"
                  size="small"
                />
              )}
            />
            <FormHelperText>{errors.country?.message}</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>

      <Box my={2} display="flex" justifyContent="flex-end">
        <Button style={{ marginRight: 16 }} onClick={onClose}>
          Cancel
        </Button>
        <Button 
          disabled={!stripe || posting || !autoReloadPreference.isValid}
          type="submit" 
          color="primary" 
          variant="contained" 
          disableElevation
        >
          {posting ? 'Submitting...' : 'Submit'}
        </Button>
      </Box>
    </form>
  );
};

export default PaymentForm;
